import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';
import { useBooleanState } from '../../hooks';
import useIsMobile from '../../hooks/useIsMobile';
import FingoTableCell from './FingoTableCell';
import FingoTableMobileLayout from './FingoTableMobileLayout';
import { DEFAULT_TABLE_CLASS_NAME } from '../../theme/constants/table';

const FingoTableRow = ({
  columns,
  row,
  checkboxSelection,
  isRowSelectable,
  selectionModel,
  onSelectionModelChange,
  collapsible,
  isRowCollapsible,
  collapseComponent: CollapseComponent,
  initialExpandedRow,
  useMobileLayout,
  tableClassName,
}) => {
  const [open, toggleOpen] = useBooleanState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (row.id === initialExpandedRow) {
      toggleOpen();
    }
  }, [row.id, initialExpandedRow]);

  const selectClickHandler = (event, id) => {
    const isChecked = !!event.target?.checked;
    if (isChecked) {
      onSelectionModelChange([...selectionModel, id]);
    } else {
      onSelectionModelChange(selectionModel.filter((rowId) => rowId !== id));
    }
  };

  const collapsibleRow = collapsible && isRowCollapsible({ row });
  const selectableRow = checkboxSelection && isRowSelectable({ row });
  return (isMobile && useMobileLayout)
    ? (
      <FingoTableMobileLayout
        columns={columns}
        row={row}
        checkboxSelection={checkboxSelection}
        isRowSelectable={isRowSelectable}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        collapsible={collapsible}
        isRowCollapsible={isRowCollapsible}
        collapseComponent={CollapseComponent}
        open={open}
        toggleOpen={toggleOpen}
        tableClassName={tableClassName}
      />
    ) : (
      <>
        <TableRow hover role="checkbox" tabIndex={-1}>
          {(collapsible || checkboxSelection) && (
          <TableCell padding="checkbox" sx={{ minWidth: collapsibleRow && selectableRow ? '70px' : null }}>
            {selectableRow && (
            <Checkbox
              color="primary"
              checked={selectionModel.includes(row.id)}
              indeterminate={!isRowSelectable({ row })}
              disabled={!isRowSelectable({ row })}
              onChange={(event) => selectClickHandler(event, row.id)}
            />
            )}
            {collapsibleRow && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => {
                toggleOpen();
              }}
            >
              {open ? <KeyboardArrowUp className={DEFAULT_ICON_CLASS_NAME} />
                : <KeyboardArrowDown className={DEFAULT_ICON_CLASS_NAME} />}
            </IconButton>
            )}
          </TableCell>
          )}
          {columns.map(({
            type, field, renderCell, valueGetter, width, align, columnClassName,
          }) => (
            <FingoTableCell
              key={`${row.id}-${field}`}
              type={type}
              row={row}
              renderCell={renderCell}
              valueGetter={valueGetter}
              width={width}
              align={align}
              tableClassName={tableClassName}
              columnClassName={columnClassName}
            />
          ))}
        </TableRow>
        {collapsibleRow && open && (
        <TableRow hover role="checkbox" tabIndex={-1}>
          <TableCell style={{ padding: 0 }} colSpan={columns.length + 1}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <CollapseComponent row={row} />
            </Collapse>
          </TableCell>
        </TableRow>
        )}
      </>
    );
};

FingoTableRow.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string,
      sortable: PropTypes.bool,
      renderCell: PropTypes.func,
      valueGetter: PropTypes.func,
      columnClassName: PropTypes.string,
      align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
    }),
  ).isRequired,
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  checkboxSelection: PropTypes.bool.isRequired,
  isRowSelectable: PropTypes.func.isRequired,
  selectionModel: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionModelChange: PropTypes.func.isRequired,
  collapsible: PropTypes.bool.isRequired,
  isRowCollapsible: PropTypes.func.isRequired,
  collapseComponent: PropTypes.func.isRequired,
  initialExpandedRow: PropTypes.string,
  useMobileLayout: PropTypes.bool,
  tableClassName: PropTypes.string,
};

FingoTableRow.defaultProps = {
  initialExpandedRow: null,
  useMobileLayout: false,
  tableClassName: DEFAULT_TABLE_CLASS_NAME,
};

export default FingoTableRow;
